import { config } from "../config";
import { fetchWithAuthRetry } from "../utils/fetchWithAuthRetry";
import { TSession } from "../utils/types";

const { protocol, host } = config.coreApi;
const baseUrl = `${protocol}://${host}/clinics/v1/image-intelligence`;

export type TDuplicateImageCandidate = {
  image_id: string;
  s3_key: string;
  file_size: number;
  image_url: string;
};

export type TDuplicateGroup = {
  file_size: number;
  images: TDuplicateImageCandidate[];
};

export type TDuplicatePatient = {
  patient_uuid: string;
  full_name: string;
  duplicate_groups: TDuplicateGroup[];
};

export type TDuplicatePatientsResponse = {
  patients: TDuplicatePatient[];
  next_cursor?: string | null;
};

export type TMergeDuplicateImagesPayload = {
  patient_uuid: string;
  keep_image_id: string;
  delete_image_ids: string[];
};

export const getDuplicateImagePatients = async (
  session: TSession,
  limit = 10,
  cursor?: string,
): Promise<TDuplicatePatientsResponse> => {
  if (!session?.clinicId) {
    throw new Error("Clinic ID is required to fetch duplicate images.");
  }

  const queryParams = new URLSearchParams({ limit: `${Math.min(50, limit)}` });
  if (cursor) {
    queryParams.append("cursor", cursor);
  }

  const response = await fetchWithAuthRetry(
    `${baseUrl}/${session.clinicId}/duplicate-images?${queryParams.toString()}`,
  );

  if (!response.ok) {
    const errorText = await response.text().catch(() => "Unknown error");
    throw new Error(
      `Failed to fetch duplicate images: ${response.status} ${errorText}`,
    );
  }

  return response.json();
};

export const mergeDuplicateImages = async (
  session: TSession,
  payload: TMergeDuplicateImagesPayload,
) => {
  if (!session?.clinicId) {
    throw new Error("Clinic ID is required to merge duplicate images.");
  }

  const response = await fetchWithAuthRetry(
    `${baseUrl}/${session.clinicId}/duplicate-images/merge`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );

  if (!response.ok) {
    const errorText = await response.text().catch(() => "Unknown error");
    throw new Error(
      `Failed to merge duplicate images: ${response.status} ${errorText}`,
    );
  }

  return response.json();
};
